import { Suspense, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Link, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import useApplications from "../../../clients/applications/useApplications";
import * as UI from "../../../modules/ui-kit";
import {
    confirmUnsavedChanges,
    type UnsavedChangesContext,
} from "../../../modules/unsaved-changes";

export default function Applications() {
    const [token, setToken] = useState("");

    const { applicationID } = useParams();

    const guard = useOutletContext<UnsavedChangesContext>();

    return (
        <>
            <Styled>
                <UI.HeroInputField>
                    <div>
                        <UI.HeroInput
                            autoFocus={ !applicationID }
                            onChange={ setToken }
                            placeholder="Search"
                            value={ token }
                        />
                        <button
                            onClick={ () => setToken("") }
                            type="reset"
                        >
                            <IoClose />
                        </button>
                    </div>
                    <span>search Applications by domain or name</span>
                </UI.HeroInputField>
                <UI.A as={ Link } to="./new">create new application</UI.A>
                <Suspense fallback={ <span>loading</span> }>
                    <ApplicationList token={ token } guard={ guard } />
                </Suspense>
            </Styled>
            <Outlet context={ guard satisfies UnsavedChangesContext } />
        </>
    );
}

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: opacity 200ms;

    ${ UI.HeroInput } {
        font-size: 2.4em;
    }

    ol {
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;

        a {
            color: #000;
            font-size: 1.2rem;
            overflow: hidden;
            position: relative;
            text-decoration: none;
            text-overflow: ellipsis;
            white-space: nowrap;

            ${ UI.FluidAccent2 }

            &:hover {
                text-decoration: none;
            }
        }
    }
`;

function ApplicationList(props) {
    const { token, guard } = props;

    const navigate = useNavigate();
    const { applicationDomain } = useParams();

    async function handleSelect(event, domain: string) {
        event.preventDefault();
        if (domain === applicationDomain) return;
        if (await confirmUnsavedChanges(guard)) navigate(domain);
    }

    const applications = useApplications()
        .filter(
            (application) => [
                application.domain.toLowerCase().includes(token.toLowerCase()),
                application.name?.toLowerCase?.()?.includes?.(token.toLowerCase()),
            ].some(Boolean)
        )
        .sort(
            (a, b) => a.name?.localeCompare?.(b.name)
        );

    if (!applications.length) return <span>no applications</span>;

    return (
        <ol>
            {
                applications.map(
                    (application) => (
                        <li key={ application.domain }>
                            <a
                                href={ application.domain }
                                onClick={ (event) => handleSelect(event, application.domain) }
                            >
                                { application.name }
                                <span> [{ application.domain }.]</span>
                            </a>
                        </li>
                    )
                )
            }
        </ol>
    );
}
